import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { GAME_HEIGHT, GAME_WIDTH } from "../constants";
import type { ObstacleData, PickupData } from "../types";
import { WORLD_LIFE_CONFIG } from "../worldLifeConfig";

export interface WorldLifeLayers {
  terrain: Phaser.GameObjects.Layer;
  shadows: Phaser.GameObjects.Layer;
  obstacles: Phaser.GameObjects.Layer;
  pickups: Phaser.GameObjects.Layer;
  actors: Phaser.GameObjects.Layer;
  fog: Phaser.GameObjects.Layer;
  particles: Phaser.GameObjects.Layer;
  debug: Phaser.GameObjects.Layer;
}

export interface WorldLifeObstacleVisual {
  sprite: Phaser.GameObjects.Image;
  shadow: Phaser.GameObjects.Ellipse;
}

export interface WorldLifePickupVisual {
  halo: Phaser.GameObjects.Ellipse;
  ring: Phaser.GameObjects.Ellipse;
  icon: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
  shadow: Phaser.GameObjects.Ellipse;
}

type AmbientParticle = {
  dot: Phaser.GameObjects.Ellipse;
  x: number;
  y: number;
  depth: number;
  drift: number;
  phase: number;
  radius: number;
  tint: number;
};

type DustParticle = {
  dot: Phaser.GameObjects.Ellipse;
  age: number;
  lifetime: number;
  vx: number;
  vy: number;
};

export class WorldLifeSystem {
  private fogBack?: Phaser.GameObjects.TileSprite;
  private fogFront?: Phaser.GameObjects.TileSprite;
  private ambientParticles: AmbientParticle[] = [];
  private dustParticles: DustParticle[] = [];
  private dustCooldown = 0;

  constructor(private scene: Phaser.Scene, private layers: WorldLifeLayers) {
    if (WORLD_LIFE_CONFIG.fogOpacity > 0) {
      this.fogBack = scene.add
        .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, AssetKeys.terrain.fog)
        .setOrigin(0)
        .setAlpha(WORLD_LIFE_CONFIG.fogOpacity)
        .setTint(0x9fb8a8)
        .setTileScale(WORLD_LIFE_CONFIG.fogScale, WORLD_LIFE_CONFIG.fogScale)
        .setScrollFactor(0);
      this.fogFront = scene.add
        .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, AssetKeys.terrain.fog)
        .setOrigin(0)
        .setAlpha(WORLD_LIFE_CONFIG.fogOpacity * 0.42)
        .setTint(0x758d80)
        .setTileScale(WORLD_LIFE_CONFIG.fogScale * 1.18, WORLD_LIFE_CONFIG.fogScale * 1.18)
        .setScrollFactor(0);
      this.layers.fog.add([this.fogBack, this.fogFront]);
    }

    for (let i = 0; i < WORLD_LIFE_CONFIG.ambientParticleCount; i += 1) {
      this.createAmbientParticle(i);
    }
  }

  reset() {
    if (this.fogBack && this.fogFront) {
      this.fogBack.tilePositionX = 0;
      this.fogBack.tilePositionY = 0;
      this.fogFront.tilePositionX = GAME_WIDTH * 0.23;
      this.fogFront.tilePositionY = GAME_HEIGHT * 0.17;
    }
    this.dustCooldown = 0;
    this.dustParticles.forEach((particle) => particle.dot.destroy());
    this.dustParticles = [];
    this.ambientParticles.forEach((particle, index) => this.placeAmbientParticle(particle, index));
  }

  update(dt: number, elapsed: number, worldSpeed: number, cameraScrollDeltaY: number, player: { x: number; y: number; vx: number; vy: number }) {
    const visualDy = worldSpeed * dt - cameraScrollDeltaY;
    this.updateFog(dt, elapsed, visualDy);
    this.updateAmbientParticles(dt, elapsed, visualDy);
    this.updateFootDust(dt, player);
    this.updateDustParticles(dt);
  }

  applyCameraBreathing(baseScrollY: number, elapsed: number) {
    if (!WORLD_LIFE_CONFIG.cameraBreathingEnabled) return baseScrollY;
    return baseScrollY + Math.sin(elapsed * WORLD_LIFE_CONFIG.cameraBreathingSpeed) * WORLD_LIFE_CONFIG.cameraBreathingAmount;
  }

  applyObstacleLife(obstacle: ObstacleData, visual: WorldLifeObstacleVisual, elapsed: number) {
    if (!this.isOrganicObstacle(obstacle)) return;
    const seed = this.hash(obstacle.id);
    const speed = WORLD_LIFE_CONFIG.organicSwaySpeed + seed * WORLD_LIFE_CONFIG.organicSwayRandomness * 0.12;
    const sway = Math.sin(elapsed * speed + seed * 10) * WORLD_LIFE_CONFIG.organicSwayAmount;
    visual.sprite.rotation += sway;
    visual.sprite.scaleX *= 1 + Math.abs(sway) * 0.38;
    visual.sprite.scaleY *= 1 - Math.abs(sway) * 0.22;
  }

  applyPickupGlow(pickup: PickupData, visual: WorldLifePickupVisual, elapsed: number) {
    const seed = this.hash(pickup.id);
    const wave = (Math.sin(elapsed * WORLD_LIFE_CONFIG.glowPulseSpeed + seed * 6) + 1) * 0.5;
    const alpha = Phaser.Math.Linear(WORLD_LIFE_CONFIG.glowMinAlpha, WORLD_LIFE_CONFIG.glowMaxAlpha, wave);
    const scale = 1 + WORLD_LIFE_CONFIG.glowPulseAmount * (wave - 0.5);
    const baseScale = pickup.visualScale ?? 1;
    visual.icon.setAlpha(alpha);
    if (!(visual.icon instanceof Phaser.GameObjects.Sprite)) {
      visual.icon.setDisplaySize(48 * baseScale * scale, 48 * baseScale * scale);
    }
    visual.halo.setAlpha(0.1 + wave * 0.08).setSize((70 + wave * 8) * baseScale, (70 + wave * 8) * baseScale);
    visual.ring.setAlpha(0.42 + wave * 0.2).setSize((58 + wave * 4) * baseScale, (58 + wave * 4) * baseScale);
    visual.shadow.setAlpha(0.24 + wave * 0.08);
  }

  applyGhostLife(
    ghost: {
      container: Phaser.GameObjects.Container;
      sprite: Phaser.GameObjects.Image;
      aura: Phaser.GameObjects.Image;
      trail: Phaser.GameObjects.Image;
    },
    index: number,
    elapsed: number,
    intensity: number
  ) {
    const wave = (Math.sin(elapsed * WORLD_LIFE_CONFIG.ghostFloatSpeed + index * 0.73) + 1) * 0.5;
    ghost.container.y += Math.sin(elapsed * WORLD_LIFE_CONFIG.ghostFloatSpeed + index) * WORLD_LIFE_CONFIG.ghostFloatAmount * 0.35;
    ghost.sprite.setAlpha(0.78 + wave * 0.08 + intensity * 0.05);
    ghost.aura.setAlpha(WORLD_LIFE_CONFIG.ghostAuraAlpha * (0.72 + wave * 0.38) + intensity * 0.04);
    ghost.trail.setAlpha(0.1 + intensity * 0.05);
  }

  private updateFog(dt: number, elapsed: number, visualDy: number) {
    if (!this.fogBack || !this.fogFront) return;
    const drift = WORLD_LIFE_CONFIG.fogSpeed * dt;
    this.fogBack.tilePositionY -= visualDy * WORLD_LIFE_CONFIG.fogDepth + drift;
    this.fogBack.tilePositionX += Math.sin(elapsed * 0.15) * dt * 3;
    this.fogFront.tilePositionY -= visualDy * WORLD_LIFE_CONFIG.fogDepth * 0.55 + drift * 0.42;
    this.fogFront.tilePositionX -= Math.cos(elapsed * 0.12) * dt * 2.2;
  }

  private updateAmbientParticles(dt: number, elapsed: number, visualDy: number) {
    this.ambientParticles.forEach((particle) => {
      particle.y += visualDy * particle.depth + WORLD_LIFE_CONFIG.ambientParticleSpeed * dt * particle.depth;
      particle.x += Math.sin(elapsed * 0.42 + particle.phase) * particle.drift * dt;
      if (particle.y > GAME_HEIGHT + 28) particle.y = -28;
      if (particle.x < -24) particle.x = GAME_WIDTH + 24;
      if (particle.x > GAME_WIDTH + 24) particle.x = -24;
      const pulse = 0.72 + Math.sin(elapsed * 0.9 + particle.phase) * 0.28;
      particle.dot
        .setPosition(particle.x, particle.y)
        .setSize(particle.radius * 2, particle.radius * 2)
        .setFillStyle(particle.tint, WORLD_LIFE_CONFIG.ambientParticleOpacity * pulse);
    });
  }

  private updateFootDust(dt: number, player: { x: number; y: number; vx: number; vy: number }) {
    if (!WORLD_LIFE_CONFIG.footDustEnabled) return;
    const speed = Math.hypot(player.vx, player.vy);
    this.dustCooldown -= dt;
    if (speed < 80 || this.dustCooldown > 0) return;

    this.dustCooldown = WORLD_LIFE_CONFIG.footDustRate;
    const dot = this.scene.add.ellipse(
      player.x + Phaser.Math.Between(-16, 16),
      player.y + WORLD_LIFE_CONFIG.footDustOffsetY + Phaser.Math.Between(-3, 5),
      8,
      4,
      0x1b1712,
      WORLD_LIFE_CONFIG.footDustOpacity
    );
    this.layers.particles.add(dot);
    this.dustParticles.push({
      dot,
      age: 0,
      lifetime: WORLD_LIFE_CONFIG.footDustLifetime,
      vx: Phaser.Math.Between(-12, 12),
      vy: Phaser.Math.Between(8, 26)
    });
  }

  private updateDustParticles(dt: number) {
    this.dustParticles = this.dustParticles.filter((particle) => {
      particle.age += dt;
      const t = particle.age / particle.lifetime;
      if (t >= 1) {
        particle.dot.destroy();
        return false;
      }
      particle.dot.x += particle.vx * dt;
      particle.dot.y += particle.vy * dt;
      particle.dot.setAlpha(WORLD_LIFE_CONFIG.footDustOpacity * (1 - t));
      particle.dot.setScale(1 + t * 0.9, 1 + t * 0.35);
      return true;
    });
  }

  private createAmbientParticle(index: number) {
    const tint = index % 3 === 0 ? WORLD_LIFE_CONFIG.ambientParticleTint : index % 3 === 1 ? 0x2b2119 : 0x7fa86a;
    const dot = this.scene.add.ellipse(0, 0, 3, 3, tint, WORLD_LIFE_CONFIG.ambientParticleOpacity).setScrollFactor(0);
    this.layers.particles.add(dot);
    const particle: AmbientParticle = {
      dot,
      x: 0,
      y: 0,
      depth: WORLD_LIFE_CONFIG.ambientParticleDepth * Phaser.Math.FloatBetween(0.45, 1.25),
      drift: Phaser.Math.FloatBetween(2, 12),
      phase: Phaser.Math.FloatBetween(0, Math.PI * 2),
      radius: Phaser.Math.FloatBetween(1.1, 2.4),
      tint
    };
    this.ambientParticles.push(particle);
    this.placeAmbientParticle(particle, index);
  }

  private placeAmbientParticle(particle: AmbientParticle, index: number) {
    const band = index / Math.max(1, WORLD_LIFE_CONFIG.ambientParticleCount - 1);
    particle.x = Phaser.Math.FloatBetween(24, GAME_WIDTH - 24);
    particle.y = Phaser.Math.Linear(-20, GAME_HEIGHT + 20, band);
    particle.dot.setPosition(particle.x, particle.y);
  }

  private isOrganicObstacle(obstacle: ObstacleData) {
    return (
      obstacle.key.includes("tree") ||
      obstacle.key.includes("root") ||
      obstacle.key.includes("bush") ||
      obstacle.key.includes("thorn") ||
      obstacle.key.includes("stump") ||
      obstacle.key.includes("toxic")
    );
  }

  private hash(value: string) {
    let h = 0;
    for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) >>> 0;
    return (h % 1000) / 1000;
  }
}
